import _ from "lodash";
import L from "leaflet";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import { MapContainer, Marker, TileLayer, AttributionControl, useMapEvents, Rectangle } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Empty, Input, Spin, Button, Modal, InputNumber, Collapse, CollapseProps, Switch, Dropdown, Popover } from "antd";

import classNames from "classnames";
import { useMutation, useQuery } from "@tanstack/react-query";
import QUERY_KEYS from "services/api/queryKeys";
import { SearchOutlined, LockOutlined, LoadingOutlined, MoreOutlined, DownOutlined, WarningOutlined, DownloadOutlined, RobotOutlined, EnvironmentOutlined, FileOutlined, AimOutlined, CloseCircleOutlined, InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";

import AddressItem from "components/address/address-item";
import InsightPage from "pages/insight";
import { ReactComponent as IconLocation } from "assets/images/location-white.svg";
import { ReactComponent as IconStarContact } from "assets/images/insight.svg";

import API from "services/api";
import { CARTO_TILE_URL, CARTO_ATTRIBUTION } from "constants/map";
import { BUTTON_ID } from "constants/insight";
import { useAiMapWebSocket } from "hooks/useAiMapWebSocket";
import useCommonStore from "stores/useCommon";
import useInsight from "stores/useInsight";
import { getUserToken } from "services/storages/userStorage";
import { setLastAccessPage } from "services/storages/expiredStorage";
import { hasValidIframeKey } from "utils/iframeKey";
import PAGES from "routes/constants";
import { ADDRESS_MAP_COLOR_DPE, ADDRESS_PRIMARY_COLOR } from "constants/address";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DistributionChartInteger } from "components/dashboard/distribution-chart";

import "styles/address.scss";

const fetchMaintenance = async (setAlertTop: (alert: any) => void) => {
  try {
    const result = await API.mock.getMaintenance();
    if (result?.status === "warn" && result?.message) {
      setAlertTop({
        type: "maintenance",
        message: result.message,
        closeable: true,
      });
    }
  } catch (e) {
    // silently ignore maintenance check errors
  }
};

// Fix Leaflet default icon issue with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Normalize API response to handle both legacy and new formats
const normalizeMapResult = (item: any) => {
  // Check if it's legacy format (arrays with ["", value])
  const isLegacy = Array.isArray(item.ademe_id) || Array.isArray(item.address);

  if (isLegacy) {
    const ademeId = item.ademe_id?.[1] || "";
    const refsAdeme = item.refs_ademe?.[1] || [ademeId];
    return {
      ref_ademe: ademeId,
      ademe_id: ademeId,
      refs_ademe: refsAdeme,
      label_z: item.label_z?.[1] || 0,
      label_range: item.label_range?.[1] || [],
      count: item.count?.[1] !== undefined ? item.count[1] : (refsAdeme.length || 1),
      address: item.address?.[1] || "",
      label: item.label?.[1] || "",
      gps: item.gps?.[1] || [0, 0],
      distance: parseFloat(item.debug?.distance?.[1]) || 0,
      date_dpe: item.date_dpe?.[1] || "",
      housing_surface: item.housing_surface?.[1] || "",
      housing_add_compl: item.housing_add_compl?.[1] || "",
    };
  }

  // New format - return as-is with defaults
  // Handle both map/ai format and narrow-to-address format (different field names)
  const ademeId = item.ref_ademe || item.ademe_id || "";
  const refsAdeme = item.refs_ademe || [ademeId];
  return {
    ref_ademe: ademeId,
    ademe_id: ademeId,
    refs_ademe: refsAdeme,
    label_z: item.label_z || 0,
    label_range: item.label_range || [item.etiquette_dpe].filter(Boolean),
    count: item.count !== undefined ? item.count : (refsAdeme.length || 1),
    address: item.address || item.adresse_ban || "",
    label: item.label || item.etiquette_dpe || "",
    label_ges: item.label_ges || item.etiquette_ges || "",
    gps: item.gps || [0, 0],
    distance: item.distance || 0,
    date_dpe: item.date_dpe || "",
    housing_surface: item.housing_surface || item.surface_habitable_logement || "",
    housing_add_compl: item.housing_add_compl || item.complement_adresse_logement || "",
    type_batiment: item.type_batiment || "",
    date_etablissement_dpe: item.date_etablissement_dpe || "",
  };
};

const ORLEANS_CENTER = {
  lat: 47.902964,
  lng: 1.909251,
};

const ULTRA_HIGH_ZOOM = 15;
// Address page: extra zoom levels applied on top of the debug.autobbox fit
const ADDRESS_AUTOFIT_ZOOM_OFFSET = 4;
// Address page: max zoom of that autobbox fit (offset included), centered on the searched address
const ADDRESS_AUTOFIT_MAX_ZOOM = 20;

const STREET_LEVEL_ZOOM = 19;

// Fallback DPE database update date, used until the API returns debug.sql_update_last
const DEFAULT_SQL_UPDATE_LAST = "15/09/2026";

// API may send debug.autobbox as boolean or string ("true"/"True")
const isAutoBboxEnabled = (value: any) =>
  value === true || String(value).toLowerCase() === "true";

// Bounds covering every result location ([lat, lng] in gps), or null if none
const getResultsBounds = (mapResults: any[]) => {
  const points = mapResults
    .filter((item) => Array.isArray(item.gps) && item.gps.length >= 2)
    .map((item) => [item.gps[0], item.gps[1]] as [number, number]);
  return points.length > 0 ? L.latLngBounds(points) : null;
};
// Create custom cluster icon with dominant DPE color weighted by count
const createClusterIcon = (cluster: any) => {
  const markers = cluster.getAllChildMarkers();
  const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0 };
  let totalCount = 0;

  markers.forEach((marker: any) => {
    const className = marker.options?.icon?.options?.className || "";
    // Extract count from className (format: "... dpe-count-27")
    const countMatch = className.match(/dpe-count-(\d+)/);
    const markerCount = countMatch ? parseInt(countMatch[1], 10) : 1;
    // Extract label (format: "... dpe-X")
    const labelMatch = className.match(/dpe-([A-G])\b/);
    const label = labelMatch ? labelMatch[1] : null;

    if (label && counts[label] !== undefined) {
      counts[label] += markerCount;
    }
    totalCount += markerCount;
  });

  // Find dominant DPE class weighted by actual counts
  const dominant = Object.keys(counts).reduce((a, b) =>
    counts[a] > counts[b] ? a : b
  );

  const color = ADDRESS_MAP_COLOR_DPE[dominant] || "#888";
  const size = totalCount > 1000 ? 52 : totalCount > 100 ? 42 : 34;
  const displayCount = totalCount > 999 ? `${Math.round(totalCount / 1000)}k` : totalCount;

  return L.divIcon({
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: ${color};
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 700;
      font-size: ${size > 42 ? 14 : 12}px;
    ">${displayCount}</div>`,
    className: "dpe-cluster-icon",
    iconSize: L.point(size, size),
  });
};

// Component to track map bounds changes
const MapBoundsHandler: React.FC<{ onBoundsChange: (bounds: L.LatLngBounds) => void }> = ({ onBoundsChange }) => {
  const map = useMapEvents({
    moveend: () => {
      onBoundsChange(map.getBounds());
    },
    zoomend: () => {
      onBoundsChange(map.getBounds());
    },
    load: () => {
      onBoundsChange(map.getBounds());
    },
  });

  // Set initial bounds and invalidate size to fix grey tiles
  React.useEffect(() => {
    onBoundsChange(map.getBounds());
    // Fix for grey/missing tiles - invalidate size after container is ready
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    // Also handle window resize
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map, onBoundsChange]);

  return null;
};

const AnimatedSequence: React.FC<{ word: string }> = ({ word }) => {
  const chars = word.split("").concat([".", ".", "."]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % chars.length);
    }, 200);
    return () => clearInterval(interval);
  }, [chars.length]);

  return (
    <span>
      {chars.map((char, i) => (
        <span
          key={i}
          style={{
            fontWeight: i === activeIndex ? 700 : 400,
            color: i === activeIndex ? "var(--address-primary, #1890ff)" : "inherit",
            transition: "all 0.15s ease",
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
};

const renderLogLine = (log: string, isLast: boolean) => {
  if (isLast && log.endsWith("...")) {
    const textWithoutDots = log.slice(0, -3);
    const lastSpaceIndex = textWithoutDots.lastIndexOf(" ");
    if (lastSpaceIndex >= 0) {
      const prefix = textWithoutDots.slice(0, lastSpaceIndex + 1);
      const lastWord = textWithoutDots.slice(lastSpaceIndex + 1);
      return (
        <>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
            {prefix}
          </ReactMarkdown>
          <AnimatedSequence word={lastWord} />
        </>
      );
    }
    return <AnimatedSequence word={textWithoutDots} />;
  }
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>{log}</ReactMarkdown>;
};

const AI_SEARCH_SUGGESTIONS = [
  "Passoires Paris (DPE F/G)",
  "DPE récents rue Réaumur, Paris",
  "Appartement +100m² DPE F/G Marseille",
  "DPE G Nantes",
  "Maisons A/B Lyon",
  "DPE mois dernier Bordeaux",
];

const progressSteps = [
  { title: "Sélection du DPE", isFinished: true },
  { title: "Analyse du DPE", isFinished: false },
  { title: "L'offre checkDPE", isFinished: false },
  { title: "Votre rapport personnalisé", isFinished: false },
];

interface AiMapPageProps {
  defaultSearchMode?: 'ai' | 'address' | 'file';
  isIframe?: boolean;
}

function AiMapPage({ defaultSearchMode = 'ai', isIframe = false }: AiMapPageProps) {
  const { address } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef<any>();
  const clusterRef = useRef<any>();
  const markerRefs = useRef<Map<string, L.Marker>>(new Map());
  const listRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const isLoggedIn = !!getUserToken();
  // Embedded map can be opened without login when a valid ?key= is provided in the URL
  const hasIframeKeyAccess = isIframe && hasValidIframeKey();
  // The /address/ page is public; only the AI map is restricted to members
  const isPublicAccess = defaultSearchMode === 'address' && !isIframe;

  const { setIsLoadedPage, setAlertTop } = useCommonStore();
  const { setButtonId } = useInsight();

  useEffect(() => {
    if (isIframe) return;
    fetchMaintenance(setAlertTop);
    return () => {
      setAlertTop(null);
    };
  }, [isIframe, setAlertTop]);

  const [searchValue, setSearchValue] = useState(address || "");
  const [isInsightModalOpen, setIsInsightModalOpen] = useState(false);

  const handleToInsight = useCallback(() => {
    setButtonId(BUTTON_ID.CONTACTEZ);
    setIsInsightModalOpen(true);
  }, [setButtonId]);
  const [addressSearchValue, setAddressSearchValue] = useState(address || "");
  const [selectedAddressCoords, setSelectedAddressCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchMode, setSearchMode] = useState<'ai' | 'address' | 'file'>(defaultSearchMode);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [gps, setGps] = useState<any[]>([]);
  const [resultAutocomplete, setResultAutocomplete] = useState<any[]>([]);
  const [pendingAutoExpand, setPendingAutoExpand] = useState<string | null>(null);
  const [center, setCenter] = useState<any[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState("");
  const [activeCollapseKeys, setActiveCollapseKeys] = useState<string[]>([]);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState<string | null>(null);
  const [loadingCount, setLoadingCount] = useState<number>(0);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [batchSize, setBatchSize] = useState(() => {
    const saved = localStorage.getItem(isIframe ? "aiMap_batchSize_iframe" : "aiMap_batchSize");
    const defaultBatchSize = isIframe
      ? Number(process.env.REACT_APP_IFRAME_BATCH_SIZE) || 5000
      : Number(process.env.REACT_APP_BATCH_SIZE) || 25000;
    const value = saved ? parseInt(saved, 10) : defaultBatchSize;
    return Math.min(value, 30000); // Cap at 30K
  });
  const [model, _setModel] = useState(() => {
    return localStorage.getItem("aiMap_model") || "gpt-5";
  });
  const [realTimeLogs, setRealTimeLogs] = useState(() => {
    const saved = localStorage.getItem("aiMap_realTimeLogs");
    return saved !== "false";
  });
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
  const [searchBbox, setSearchBbox] = useState<[[number, number], [number, number]] | null>(null);
  // Map view (bounds) the latest results were fetched for - used to detect when the user has panned/zoomed away from it
  const resultsViewBoundsRef = useRef<L.LatLngBounds | null>(null);
  const [searchNotification, setSearchNotification] = useState<string | null>(null);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [wsNotif, setWsNotif] = useState<string | null>(null);
  const wsNotifTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [wsLogs, setWsLogs] = useState<string[]>([]);
  const [wsReconnecting, setWsReconnecting] = useState(false);
  const [wsReconnectFailed, setWsReconnectFailed] = useState(false);
  const boundsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchIdRef = useRef<string>(crypto.randomUUID());
  // search_id last returned by the backend - resent when re-exploring the current view (map_explore)
  const lastReceivedSearchIdRef = useRef<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<{ sql_description?: string; sql?: string } | null>(null);
  // Date of the last DPE database update, provided by the API as debug.sql_update_last (e.g. "12/07/2026")
  const [sqlUpdateLast, setSqlUpdateLast] = useState<string>(DEFAULT_SQL_UPDATE_LAST);

  // Autocomplete for address search mode
  const { data: dataAutoComplete, isFetching: isFetchingAutoComplete } = useQuery({
    queryKey: [QUERY_KEYS.AUTOCOMPLETE_SEARCH, addressSearchValue],
    queryFn: () => API.mock.autoCompleteSearch(addressSearchValue),
    enabled: searchMode === 'address' && addressSearchValue.length >= 3,
  });

  const addressAutoCompleteOptions = useMemo(() => {
    if (!dataAutoComplete?.features) return [];
    return dataAutoComplete.features.map((feature: any) => ({
      value: feature.properties.label,
      label: feature.properties.label,
      data: feature,
    }));
  }, [dataAutoComplete]);

  const { connect: connectWs, disconnect: disconnectWs } = useAiMapWebSocket({
    onMessage: (data: any) => {
      console.log("[WebSocket] message:", data);
      if (data.data?.logs) {
        setWsLogs(data.data.logs);
      }
      if (data.data?.notif) {
        if (wsNotifTimeoutRef.current) {
          clearTimeout(wsNotifTimeoutRef.current);
        }
        setWsNotif(data.data.notif);
        const notifTime = Number(process.env.REACT_APP_NOTIF_TIME) || 5000;
        wsNotifTimeoutRef.current = setTimeout(() => {
          setWsNotif(null);
        }, notifTime);
      }
    },
    onOpen: () => {
      console.log("[WebSocket] connected, searchId:", searchIdRef.current);
      setWsReconnecting(false);
      setWsReconnectFailed(false);
    },
    onClose: () => {
      console.log("[WebSocket] disconnected");
    },
    onError: (error) => {
      console.error("[WebSocket] error:", error);
    },
    onReconnecting: (attempt) => {
      console.log("[WebSocket] reconnecting, attempt:", attempt);
      setWsReconnecting(true);
    },
    onReconnectFailed: () => {
      console.error("[WebSocket] reconnect failed, giving up");
      setWsReconnecting(false);
      setWsReconnectFailed(true);
    },
  });

  // Connect WebSocket on page load, reconnect when realTimeLogs changes
  useEffect(() => {
    disconnectWs();
    setWsReconnecting(false);
    setWsReconnectFailed(false);
    connectWs({ action: realTimeLogs ? "subscribe" : "coldSubscribe", query: { id: searchIdRef.current } });
  }, [connectWs, disconnectWs, realTimeLogs]);

  
  // Frontend version from env
  const frontendVersion = process.env.REACT_APP_COMMIT_HASH ||
    process.env.REACT_APP_GIT_SHA ||
    process.env.REACT_APP_GIT_COMMIT ||
    "";

  // Debounced bounds update handler
  const handleBoundsChange = useCallback((bounds: L.LatLngBounds) => {
    if (boundsDebounceRef.current) {
      clearTimeout(boundsDebounceRef.current);
    }
    boundsDebounceRef.current = setTimeout(() => {
      setMapBounds(bounds);

      // Show the refresh button as soon as the viewport differs from the area the results were fetched for
      // (including zooming in, where the new area is contained in the previous one)
      const resultsView = resultsViewBoundsRef.current;
      if (resultsView) {
        setShowRefreshButton(!resultsView.equals(bounds));
      }
    }, 300);
  }, []);

  // Snapshot the map view once it has settled after a search re-centers it - becomes the new baseline for drift detection
  const captureResultsView = useCallback(() => {
    setTimeout(() => {
      if (mapRef.current) {
        resultsViewBoundsRef.current = mapRef.current.getBounds();
      }
      setShowRefreshButton(false);
    }, 350);
  }, []);

  // Compute top offset for stacked notifications
  const topBarOffset = useMemo(() => {
    let offset = 0;
    if (versionMismatch) offset += 2.5;
    if (searchError) offset += 2.5;
    return offset;
  }, [versionMismatch, searchError]);

  // Filter results to only show items visible in current map viewport
  const visibleResults = useMemo(() => {
    if (!mapBounds || resultAutocomplete.length === 0) {
      return resultAutocomplete;
    }
    return resultAutocomplete.filter((item) => {
      if (!item.gps || item.gps.length < 2) return false;
      return mapBounds.contains([item.gps[0], item.gps[1]]);
    });
  }, [mapBounds, resultAutocomplete]);

  // Check if any visible address is actually expanded
  const hasExpandedPanel = useMemo(() => {
    if (activeCollapseKeys.length === 0) return false;
    const visibleAddresses = new Set(visibleResults.map((item) => item.address));
    return activeCollapseKeys.some((key) => visibleAddresses.has(key));
  }, [activeCollapseKeys, visibleResults]);

  // Calculate DPE distribution for chart (based on visible results in bbox)
  const distributionData = useMemo(() => {
    if (visibleResults.length === 0) return null;

    const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0 };
    visibleResults.forEach((item) => {
      const label = item.label?.toUpperCase();
      if (label && counts[label] !== undefined) {
        counts[label] += item.count || 1;
      }
    });

    const total = visibleResults.reduce((sum, item) => sum + (item.count || 1), 0);
    // Truncation is about the overall fetch hitting batch_size, not just what's visible in the viewport
    const totalFetched = resultAutocomplete.reduce((sum, item) => sum + (item.count || 1), 0);
    const isTruncated = totalFetched >= batchSize - 1;
    const subtitle = `${total} DPE sur la zone${isTruncated ? " — des résultats sont manquants !" : ""}`;
    return {
      distribution_neighbors: {
        title: "Distribution des DPE",
        subtitle,
        values: counts,
      },
    };
  }, [visibleResults, resultAutocomplete, batchSize]);

  // Calculate search stats (total DPEs and surface area)
  const searchStats = useMemo(() => {
    if (resultAutocomplete.length === 0) return null;

    // Total DPE count (sum of all counts)
    const totalDpes = resultAutocomplete.reduce((sum, item) => sum + (item.count || 1), 0);

    // Check if results are truncated (hit batch_size limit or batch_size - 1)
    const isTruncated = totalDpes >= batchSize - 1;

    // Surface area only available when the API returned a search bbox
    let surfaceStr: string | null = null;
    if (searchBbox) {
      // Calculate bbox surface area in km²
      const latDiff = searchBbox[1][0] - searchBbox[0][0];
      const lngDiff = searchBbox[1][1] - searchBbox[0][1];
      const avgLat = (searchBbox[0][0] + searchBbox[1][0]) / 2;
      // Convert to km (approximate: 1 degree lat ≈ 111km, 1 degree lng ≈ 111km * cos(lat))
      const latKm = latDiff * 111;
      const lngKm = lngDiff * 111 * Math.cos(avgLat * Math.PI / 180);
      const areaKm2 = latKm * lngKm;

      // Format surface
      if (areaKm2 < 1) {
        surfaceStr = `${Math.round(areaKm2 * 1000000)} m²`;
      } else if (areaKm2 < 10) {
        surfaceStr = `${areaKm2.toFixed(2)} km²`;
      } else {
        surfaceStr = `${Math.round(areaKm2)} km²`;
      }
    }

    return { totalDpes, surfaceStr, isTruncated };
  }, [searchBbox, resultAutocomplete, batchSize]);

  useEffect(() => {
    localStorage.setItem(isIframe ? "aiMap_batchSize_iframe" : "aiMap_batchSize", String(batchSize));
  }, [batchSize, isIframe]);

  useEffect(() => {
    localStorage.setItem("aiMap_model", model);
  }, [model]);

  const handleSearch = async (value: string, addressCoords?: { lat: number; lon: number }, keepZoom?: boolean, emptyQuery?: boolean, mapExplore?: boolean) => {
    // File mode: upload to S3
    if (searchMode === 'file' && droppedFile) {
      try {
        setWsLogs(['📤 Téléchargement du fichier...']);

        // Get signed S3 URL and fields
        const signedUrlResponse = await API.mock.getDataUpload(droppedFile.name);
        const signedUrl = signedUrlResponse?.url;
        const fields = signedUrlResponse?.fields;

        if (!signedUrl || !fields) {
          throw new Error('No signed URL or fields received');
        }

        setWsLogs(['📤 Téléchargement du fichier...', '⬆️ Envoi vers le serveur...']);

        // Upload file to S3 with multipart form-data
        const formData = new FormData();
        // Add all the signed fields first
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        // Add the file last
        formData.append('file', droppedFile);

        await fetch(signedUrl, {
          method: 'POST',
          body: formData,
        });

        setWsLogs(['📤 Téléchargement du fichier...', '⬆️ Envoi vers le serveur...', '✅ Fichier envoyé avec succès']);

        // TODO: Call processing API with the uploaded file key

      } catch (error: any) {
        console.error('File upload error:', error);
        setWsLogs([`❌ Erreur: ${error?.message || 'Erreur inconnue'}`]);
        setSearchError('Échec du téléchargement du fichier');
      }
      return;
    }

    if (value.trim()) {
      // Use pre-generated search ID for linking WebSocket and HTTP request
      const searchId = searchIdRef.current;
      setWsLogs([]);

      // Update URL with current search query (preserve route type)
      const basePath = isIframe ? '/ai-map-iframe' : (defaultSearchMode === 'address' ? '/address' : '/ai-map');
      navigate(`${basePath}/${encodeURIComponent(value.trim())}`, { replace: true });

      // Address mode: use old flow (gouv API -> search_dpes_map) when defaultSearchMode is 'address'
      if (searchMode === 'address') {
        try {
          setSearchNotification(null);
          setShowRefreshButton(false);
          setSearchError(null);

          // Get coordinates: use passed coords, or call gouv API for address route
          let coords = addressCoords;

          if (!coords && emptyQuery) {
            // Zone refresh: center on the current map view, not the typed address —
            // the backend ignores the bbox when lat/lon falls outside it
            coords = {
              lat: mapRef.current?.getCenter()?.lat || ORLEANS_CENTER.lat,
              lon: mapRef.current?.getCenter()?.lng || ORLEANS_CENTER.lng,
            };
          } else if (!coords && defaultSearchMode === 'address') {
            // Call gouv API to get coordinates from address text
            const autoCompleteResult = await mutateAutoCompleteSearch(value.trim());

            if (!autoCompleteResult?.features?.length) {
              setSearchError('Aucune adresse trouvée');
              return;
            }

            const feature = autoCompleteResult.features[0];
            const geometry = feature.geometry?.coordinates;
            if (geometry) {
              coords = { lat: geometry[1], lon: geometry[0] };
              setSelectedAddressCoords(coords);
            }
          }

          // Fallback: use existing coords or map center
          if (!coords) {
            coords = selectedAddressCoords || {
              lat: mapRef.current?.getCenter()?.lat || ORLEANS_CENTER.lat,
              lon: mapRef.current?.getCenter()?.lng || ORLEANS_CENTER.lng,
            };
          }

          // Only a zone refresh ("search this area") is bounded by the current view. A new address
          // search must not depend on where the map happens to be (else each "Rechercher" narrows
          // the results to the previous view and zooms in further) - the backend then uses its radius.
          const isZoneRefresh = !!(mapExplore || emptyQuery);
          const mapBoundsRect = isZoneRefresh ? mapRef.current?.getBounds() : null;
          const bbox = mapBoundsRect ? {
            min_lat: mapBoundsRect.getSouth(),
            min_lon: mapBoundsRect.getWest(),
            max_lat: mapBoundsRect.getNorth(),
            max_lon: mapBoundsRect.getEast(),
          } : {};

          const result = await mutateSearchAddressDpesMap({
            query: emptyQuery ? "" : value.trim(),
            lat: coords.lat,
            lon: coords.lon,
            batch_size: batchSize,
            search_id: mapExplore && lastReceivedSearchIdRef.current ? lastReceivedSearchIdRef.current : searchId,
            ...(mapExplore ? { map_explore: true } : {}),
            ...bbox,
          });

          // Handle response format
          const rawResults = Array.isArray(result)
            ? result
            : (result?.result || result?.map_results || []);
          const mapResults = rawResults.map(normalizeMapResult);
          setResultAutocomplete(mapResults);
          setShowAllResults(false);

          if (result?.search_id) {
            lastReceivedSearchIdRef.current = result.search_id;
          }

          const apiBbox = result?.debug?.bbox;
          const apiCenter = result?.debug?.center;

          // Store debug info for display
          console.log("Debug info from API (address mode):", result?.debug);
          if (result?.debug?.sql_description || result?.debug?.sql) {
            setDebugInfo({
              sql_description: result.debug.sql_description,
              sql: result.debug.sql,
            });
          } else {
            setDebugInfo(null);
          }
          setSqlUpdateLast(result?.debug?.sql_update_last || DEFAULT_SQL_UPDATE_LAST);

          if (apiBbox) {
            setSearchBbox([
              [apiBbox.min_lat, apiBbox.min_lon],
              [apiBbox.max_lat, apiBbox.max_lon],
            ]);
          } else {
            setSearchBbox(null);
          }

          // Re-center map based on search type
          if (mapRef.current && !keepZoom) {
            const autoFitBounds = isAutoBboxEnabled(result?.debug?.autobbox)
              ? getResultsBounds(mapResults)
              : null;
            if (autoFitBounds && defaultSearchMode === 'address' && coords) {
              // Address page: fitting all results lands too far out - zoom in closer, centered on the address
              const fitZoom = mapRef.current.getBoundsZoom(autoFitBounds, false, L.point(0, 0));
              const zoom = Math.min(fitZoom + ADDRESS_AUTOFIT_ZOOM_OFFSET, ADDRESS_AUTOFIT_MAX_ZOOM);
              mapRef.current.setView([coords.lat, coords.lon], zoom, { animate: true });
            } else if (autoFitBounds) {
              // API asked to fit all result locations
              mapRef.current.fitBounds(autoFitBounds, { padding: [0, 0], maxZoom: 17 });
            } else if (defaultSearchMode === 'address' && coords) {
              // Address mode: zoom to the specific address location at street level
              mapRef.current.setView([coords.lat, coords.lon], STREET_LEVEL_ZOOM, { animate: true });
            } else if (apiBbox) {
              // Other modes: fit to API bbox
              const bboxBounds = L.latLngBounds(
                [apiBbox.min_lat, apiBbox.min_lon],
                [apiBbox.max_lat, apiBbox.max_lon]
              );
              const fitOptions = { padding: [20, 20], maxZoom: 14 };
              mapRef.current.fitBounds(bboxBounds, fitOptions);
            }
          }
          captureResultsView();

          // Warn when the batch_size limit was hit - some results are missing from the zone
          const totalDpes = mapResults.reduce((sum: number, item: any) => sum + (item.count || 1), 0);
          if (totalDpes >= batchSize - 1) {
            setSearchNotification("Résultats partiels, affinez votre recherche");
            setTimeout(() => setSearchNotification(null), 4000);
          }

          // Update center state from API response or first result
          if (apiCenter) {
            setCenter([apiCenter.lon, apiCenter.lat]);
          } else if (mapResults.length > 0) {
            const firstResult = mapResults[0];
            setCenter([firstResult.gps[1], firstResult.gps[0]]);
          }

          setGps(
            _.uniqBy(
              mapResults.map((item: any) => ({
                label: item.label,
                lat: item.gps[0],
                lng: item.gps[1],
                ademe_id: item.ref_ademe,
                refs_ademe: item.refs_ademe,
                label_range: item.label_range,
                count: item.count,
                label_z: item.label_z,
                address: item.address,
                distance: item.distance,
              })),
              (item: any) => `${item.lat}-${item.lng}`,
            ),
          );

          setActiveCollapseKeys([]);

          // Set pending auto-expand for the search query (will be handled by useEffect)
          if (mapResults.length > 0 && !keepZoom) {
            setPendingAutoExpand(value.trim());
          }
        } catch (error: any) {
          console.error('Address search error:', error);
          setResultAutocomplete([]);
          setGps([]);
          setSearchBbox(null);
          const errorMsg = `❌ Erreur: ${error?.message || 'Erreur inconnue'}`;
          setWsLogs([errorMsg]);
          setSearchError('La recherche a échoué — veuillez réessayer');
        }
        return;
      }

      // AI mode: existing logic
      try {
        const mapCenter = mapRef.current?.getCenter() || ORLEANS_CENTER;
        const mapBoundsRect = mapRef.current?.getBounds();
        const initialZoom = mapRef.current?.getZoom() || ULTRA_HIGH_ZOOM;
        let currentZoom = initialZoom;
        let result: any;

        // Calculate bbox that shrinks when zoom increases
        const getScaledBbox = (zoom: number) => {
          if (!mapBoundsRect) return { min_lat: undefined, min_lon: undefined, max_lat: undefined, max_lon: undefined };

          // Each zoom level shrinks bbox by 10%
          const zoomDiff = zoom - initialZoom;
          const scale = Math.pow(0.7, zoomDiff);

          const centerLat = mapCenter.lat;
          const centerLng = mapCenter.lng;
          const halfLatSpan = ((mapBoundsRect.getNorth() - mapBoundsRect.getSouth()) / 2) * scale;
          const halfLngSpan = ((mapBoundsRect.getEast() - mapBoundsRect.getWest()) / 2) * scale;

          return {
            min_lat: centerLat - halfLatSpan,
            min_lon: centerLng - halfLngSpan,
            max_lat: centerLat + halfLatSpan,
            max_lon: centerLng + halfLngSpan,
          };
        };

        // Retry with zoom+1 if results_count matches batch_size (hit the limit)
        // Set REACT_APP_MAX_ZOOM_IN to enable auto zoom-in retries (default: 0 = disabled)
        const maxZoomIn = Number(process.env.REACT_APP_MAX_ZOOM_IN) || 0;
        let zoomIterations = 0;
        setSearchNotification(null);
        setShowRefreshButton(false);
        setSearchError(null);
        while (true) {
          const bbox = getScaledBbox(currentZoom);
          result = await mutateSearchAiDpesMap({
            query: value.trim(),
            lat: mapCenter.lat,
            lon: mapCenter.lng,
            zoom: currentZoom,
            batch_size: batchSize,
            model,
            search_id: mapExplore && lastReceivedSearchIdRef.current ? lastReceivedSearchIdRef.current : searchId,
            ...(mapExplore ? { map_explore: true } : {}),
            // Scaled bbox based on current zoom
            ...bbox,
          });

          const resultsCount = result?.debug?.results_count;
          console.log(`Search at zoom ${currentZoom}: ${resultsCount} results (batch_size: ${batchSize})`);

          // If results_count < batch_size - 1, we have all results - stop retrying
          // (also retry when resultsCount === batchSize - 1, as it may still be truncated)
          if (!resultsCount || resultsCount < batchSize - 1) {
            setSearchNotification(null);
            break;
          }

          // Check if we've hit max zoom iterations
          zoomIterations += 1;
          if (zoomIterations >= maxZoomIn) {
            console.log(`Hit max zoom iterations (${maxZoomIn}), stopping retry`);
            setSearchNotification(null);
            break;
          }

          // Hit the limit - retry with zoom+1 to narrow the search area
          currentZoom += 1;
          const msg = `Trop de résultats (${resultsCount}), zoom ${currentZoom}...`;
          setSearchNotification(msg);
          setTimeout(() => setSearchNotification(null), 2000);

          // Update the map with the bbox returned by the API (more accurate than our calculated one)
          const apiBboxRetry = result?.debug?.bbox;
          if (apiBboxRetry) {
            setSearchBbox([
              [apiBboxRetry.min_lat, apiBboxRetry.min_lon],
              [apiBboxRetry.max_lat, apiBboxRetry.max_lon],
            ]);
            // Also fit the map view to the API's bbox
            if (mapRef.current) {
              const bboxBounds = L.latLngBounds(
                [apiBboxRetry.min_lat, apiBboxRetry.min_lon],
                [apiBboxRetry.max_lat, apiBboxRetry.max_lon]
              );
              mapRef.current.fitBounds(bboxBounds, { padding: [0, 0] });
            }
            // Small delay to allow React to re-render the bbox on the map
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          console.log(`Hit batch_size limit, retrying with zoom ${currentZoom}...`);
        }

        // Handle multiple response formats: direct array, { result: [...] }, or { map_results: [...] }
        const rawResults = Array.isArray(result)
          ? result
          : (result?.result || result?.map_results || []);
        // Normalize results to handle both legacy and new formats
        const mapResults = rawResults.map(normalizeMapResult);
        setResultAutocomplete(mapResults);
        setShowAllResults(false);

        if (result?.search_id) {
          lastReceivedSearchIdRef.current = result.search_id;
        }

        // Check API version against frontend version (skip for local/test builds)
        const apiVersion = result?.version;
        if (apiVersion && frontendVersion && frontendVersion !== "" && frontendVersion !== "test" && apiVersion !== frontendVersion) {
          setVersionMismatch(true);
        }

        // Store search area bbox and center from API response
        const apiBbox = result?.debug?.bbox;
        const apiCenter = result?.debug?.center;
        console.log("Search area bbox:", apiBbox, "center:", apiCenter);

        // Store debug info for display
        console.log("Debug info from API (AI mode):", result?.debug);
        if (result?.debug?.sql_description || result?.debug?.sql) {
          setDebugInfo({
            sql_description: result.debug.sql_description,
            sql: result.debug.sql,
          });
        } else {
          setDebugInfo(null);
        }
        setSqlUpdateLast(result?.debug?.sql_update_last || DEFAULT_SQL_UPDATE_LAST);

        if (apiBbox) {
          setSearchBbox([
            [apiBbox.min_lat, apiBbox.min_lon],
            [apiBbox.max_lat, apiBbox.max_lon],
          ]);
        } else {
          setSearchBbox(null);
        }
        // Reset collapse keys - all panels start collapsed, user expands via title or map click
        setActiveCollapseKeys([]);

        // Re-center map: the API bbox (debug.bbox) wins - the view matches it exactly, whatever the
        // returned items cover. Only without a bbox does debug.autobbox fit all result locations.
        const autoFitBounds = isAutoBboxEnabled(result?.debug?.autobbox)
          ? getResultsBounds(mapResults)
          : null;
        if (mapRef.current && apiBbox) {
          const bboxBounds = L.latLngBounds(
            [apiBbox.min_lat, apiBbox.min_lon],
            [apiBbox.max_lat, apiBbox.max_lon]
          );
          mapRef.current.fitBounds(bboxBounds, { padding: [0, 0] });
        } else if (mapRef.current && autoFitBounds) {
          mapRef.current.fitBounds(autoFitBounds, { padding: [0, 0], maxZoom: 17 });
        }
        captureResultsView();

        // Warn when the batch_size limit was hit - some results are missing from the zone
        const totalDpesAi = mapResults.reduce((sum: number, item: any) => sum + (item.count || 1), 0);
        if (totalDpesAi >= batchSize - 1) {
          setSearchNotification("Résultats partiels, affinez votre recherche");
          setTimeout(() => setSearchNotification(null), 4000);
        }

        // Update center state from API response or first result
        if (apiCenter) {
          setCenter([apiCenter.lon, apiCenter.lat]);
        } else if (mapResults.length > 0) {
          const firstResult = mapResults[0];
          setCenter([firstResult.gps[1], firstResult.gps[0]]);
        }

        setGps(
          _.uniqBy(
            mapResults.map((item: any) => ({
              label: item.label,
              lat: item.gps[0],
              lng: item.gps[1],
              ademe_id: item.ref_ademe,
              refs_ademe: item.refs_ademe,
              label_range: item.label_range,
              count: item.count,
              label_z: item.label_z,
              address: item.address,
              distance: item.distance,
            })),
            (item: any) => `${item.lat}-${item.lng}`,
          ),
        );
      } catch (error: any) {
        console.error("searchAiDpesMap error:", error);
        console.error("Error details:", {
          status: error?.response?.status,
          code: error?.code,
          message: error?.message,
          name: error?.name,
        });

        // Check for 504 Gateway Timeout or any timeout/network error
        const status = error?.response?.status || error?.status;
        const isTimeout = status === 504 ||
          status === 502 ||
          status === 503 ||
          error?.code === "ECONNABORTED" ||
          error?.code === "ERR_NETWORK" ||
          error?.message?.toLowerCase().includes("timeout") ||
          error?.message?.toLowerCase().includes("network");

        // Clear results on any error
        setResultAutocomplete([]);
        setGps([]);
        setSearchBbox(null);

        // Clear logs and show only error message
        const errorMsg = isTimeout
          ? "❌ La recherche n'a pas abouti (timeout)"
          : `❌ Erreur: ${error?.message || 'Erreur inconnue'}`;
        setWsLogs([errorMsg]);

        // Show persistent error notification
        setSearchError(isTimeout
          ? "La recherche a échoué — réessayez avec une zone plus petite"
          : "La recherche a échoué — veuillez réessayer"
        );
      }
    }
  };

  const {
    mutateAsync: mutateSearchAiDpesMap,
    isPending: isPendingSearchAiDpesMap,
  } = useMutation({
    mutationFn: (params: any) => isIframe
      ? API.mock.searchAiFrameDpesMap(params)
      : API.mock.searchAiDpesMap(params),
    retry: 0,
  });

  const {
    mutateAsync: mutateSearchAddressDpesMap,
    isPending: isPendingSearchAddressDpesMap,
  } = useMutation({
    mutationFn: (params: any) => API.mock.searchAddressDpesMap(params),
    retry: 0,
  });

  const { mutateAsync: mutateNarrowToAddress } = useMutation({
    mutationFn: (payload: { refs_ademe: string[]; batch_size?: number }) =>
      API.mock.narrowToAddress(payload),
    retry: 0,
  });

  // For address route: call gouv API first to get coordinates
  const {
    mutateAsync: mutateAutoCompleteSearch,
    isPending: isPendingAutoComplete,
  } = useMutation({
    mutationFn: (query: string) => API.mock.autoCompleteSearch(query),
    retry: 0,
  });

  const isPendingSearch = isPendingSearchAiDpesMap || isPendingSearchAddressDpesMap || isPendingAutoComplete;

  useEffect(() => {
    if (isPendingSearch) {
      setIsLoadedPage(true);
    }
  }, [isPendingSearch, setIsLoadedPage]);

  // Auto-search when coming from /address route with an address param (or when URL changes)
  useEffect(() => {
    if (defaultSearchMode === 'address' && address) {
      setSearchValue(address);
      setAddressSearchValue(address);
      setIsSearchFocused(false); // Close any open dropdown
      handleSearch(address); // No addressCoords passed, so gouv API will be called
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, defaultSearchMode]);

  // Auto-expand address panel when results come in and pendingAutoExpand is set
  useEffect(() => {
    if (pendingAutoExpand && resultAutocomplete.length > 0) {
      // Normalize: lowercase, trim, collapse multiple spaces
      const normalize = (s: string) => s?.toLowerCase().trim().replace(/\s+/g, ' ') || '';
      const searchNorm = normalize(pendingAutoExpand);

      // Only auto-select on EXACT match - if search is wider/more general, don't auto-select
      const exactMatch = resultAutocomplete.find(
        (item: any) => normalize(item.address) === searchNorm
      );

      if (exactMatch?.address) {
        // Exact match found - expand and zoom to it
        handleExpandAddressGroup(exactMatch.address, exactMatch.address);
      }
      // If no exact match, don't auto-select anything - let user choose from results

      // Clear the pending state
      setPendingAutoExpand(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultAutocomplete, pendingAutoExpand]);

  // Auto-scroll logs to bottom during loading (desktop only)
  useEffect(() => {
    if (listRef.current && isPendingSearch && wsLogs.length > 0) {
      // Only scroll logs on desktop
      if (window.innerWidth >= 1024) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }
  }, [wsLogs, isPendingSearch]);

  // Shared function to handle address group expansion (from panel click or map marker click)
  const handleExpandAddressGroup = async (address: string, panelKey: string) => {
    const groupedByAddress = _.groupBy(resultAutocomplete, (item) => item.address);
    const items = groupedByAddress[address];

    if (!items || items.length === 0) return;

    const firstItem = items[0];
    const expectedCount = items.reduce((sum: number, item: any) => sum + (item.count || 1), 0);

    // Find the corresponding marker in gps array (which is deduplicated by coords)
    const gpsItem = gps.find((g: any) => g.address === address);
    const markerId = gpsItem?.ademe_id || firstItem.ademe_id || firstItem.ref_ademe;

    // Select the marker for this address
    setSelectedLabelId(markerId);

    // Get coordinates (gps items have lat/lng, resultAutocomplete items have gps array)
    const lat = gpsItem?.lat ?? firstItem.gps?.[0];
    const lng = gpsItem?.lng ?? firstItem.gps?.[1];

    // Zoom just enough for the marker to leave its cluster (not all the way to max zoom)
    const hasValidCoords = typeof lat === 'number' && typeof lng === 'number';
    if (mapRef.current && hasValidCoords) {
      const marker = markerId ? markerRefs.current.get(markerId) : null;
      const clusterGroup = clusterRef.current;
      if (marker && clusterGroup?.zoomToShowLayer) {
        clusterGroup.zoomToShowLayer(marker);
      } else {
        mapRef.current.setView([lat, lng], 17, { animate: true });
      }
    }

    // Helper to scroll to the panel - simple and robust
    const scrollToPanel = (): Promise<void> => {
      return new Promise((resolve) => {
        setTimeout(() => {
          const container = listRef.current;
          if (!container) {
            resolve();
            return;
          }

          const allItems = Array.from(container.querySelectorAll(`.ant-collapse-item`));
          const targetItem = allItems.find((item) => {
            const headerText = item.querySelector('.ant-collapse-header')?.textContent;
            return headerText?.includes(address);
          });

          if (targetItem) {
            // Simple approach: scroll element into view at the start of container
            (targetItem as HTMLElement).scrollIntoView({ behavior: "auto", block: "start" });
          }
          resolve();
        }, 100);
      });
    };

    // Step 1: Expand the panel and set loading state with placeholders
    setActiveCollapseKeys([panelKey]);
    setLoadingAddress(address);
    setLoadingCount(expectedCount);

    // Step 2: Wait for panel to expand, then scroll to it
    await new Promise((resolve) => setTimeout(resolve, 50));
    await scrollToPanel();

    // Step 3: Call API to get expanded details
    const allRefsAdeme = items.flatMap((item: any) => item.refs_ademe || [item.ref_ademe]);
    if (allRefsAdeme.length > 0) {
      try {
        // POST JSON body { refs_ademe, batch_size } - address is only used locally for grouping
        const result = await mutateNarrowToAddress({
          refs_ademe: allRefsAdeme,
          batch_size: batchSize,
        });

        const expandedResults = (result?.result || result || []).map((item: any) => ({
          ...normalizeMapResult(item),
          address: address, // Keep original group address for proper grouping
        }));
        if (expandedResults.length > 0) {
          setResultAutocomplete((prev) => {
            // Find the index of the first item with this address to maintain position
            const insertIndex = prev.findIndex((item) => item.address === address);
            const otherAddresses = prev.filter((item) => item.address !== address);
            // Insert expanded results at the original position
            if (insertIndex >= 0) {
              const before = otherAddresses.slice(0, insertIndex);
              const after = otherAddresses.slice(insertIndex);
              return [...before, ...expandedResults, ...after];
            }
            return [...otherAddresses, ...expandedResults];
          });
        }
      } catch (error) {
        console.error("narrowToAddress error:", error);
      } finally {
        setLoadingAddress(null);
        setLoadingCount(0);
      }
    }
  };

  // Handle collapse panel change - detect expansion and trigger shared handler
  const handleCollapseChange = (keys: string[]) => {
    // If collapsing (no keys or fewer keys), just update state
    if (keys.length === 0 || keys.length < activeCollapseKeys.length) {
      setActiveCollapseKeys(keys);
      return;
    }

    // Expanding a panel - the key IS the address now
    const newlyExpanded = keys.filter((key) => !activeCollapseKeys.includes(key));
    if (newlyExpanded.length > 0) {
      const address = newlyExpanded[0];
      handleExpandAddressGroup(address, address);
    }
  };

  // Handle map marker click - find address and trigger shared handler
  const handleClickLabel = async (gpsItem: any) => {
    const groupedByAddress = _.groupBy(resultAutocomplete, (item) => item.address);

    // Find the address by ademe_id or refs_ademe match
    const address = Object.keys(groupedByAddress).find((addr) =>
      groupedByAddress[addr].some((item: any) =>
        item.ademe_id === gpsItem.ademe_id ||
        item.refs_ademe?.includes(gpsItem.ademe_id)
      )
    );

    if (address) {
      await handleExpandAddressGroup(address, address);
    }
  };

  // Export by address (grouped) - free
  const handleExportByAddress = useCallback(() => {
    if (visibleResults.length === 0) return;

    const headers = ['Adresse', 'DPE', 'Nombre DPE', 'Refs ADEME'];
    const rows = visibleResults.map((item) => [
      item.address || '',
      item.label || '',
      item.count || 1,
      (item.refs_ademe || [item.ref_ademe]).join(', '),
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';')),
    ].join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `export-dpe-adresses-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportOptions(false);
  }, [visibleResults]);

  
  const renderGPSItem = () => {
    if (isPendingSearch) {
      return (
        <div ref={listRef} className="flex flex-col p-4 flex-1 overflow-y-auto min-h-0">
          {(wsReconnecting || wsReconnectFailed) && (
            <div className={classNames(
              "text-xs mb-2 flex items-center gap-1",
              wsReconnectFailed ? "text-red-500" : "text-orange-500",
            )}>
              {wsReconnectFailed ? (
                <span>Connexion perdue — les logs en temps réel sont indisponibles, la recherche continue...</span>
              ) : (
                <>
                  <LoadingOutlined />
                  <span>Reconnexion en cours...</span>
                </>
              )}
            </div>
          )}
          {/* Simple loading on mobile */}
          <div className="lg:hidden text-base text-gray-400">
            <AnimatedSequence word="Chargement" />
          </div>
          {/* Detailed logs on desktop */}
          <div className="hidden lg:block">
            {wsLogs.length > 0 ? (
              <div className="text-base text-gray-500">
                {wsLogs.map((log, i) => (
                  <div key={i}>{renderLogLine(log, i === wsLogs.length - 1)}</div>
                ))}
              </div>
            ) : (
              <div className="text-base text-gray-400"><AnimatedSequence word="Chargement" /></div>
            )}
          </div>
        </div>
      );
    }

    if (!_.isEmpty(visibleResults)) {
      const maxItems = Number(process.env.REACT_APP_MAX_LEFT_LIST_ITEMS) || 10000;
      const groupedByAddress = _.groupBy(
        visibleResults,
        (item) => item.address,
      );

      const allAddresses = Object.keys(groupedByAddress);
      const tooManyResults = allAddresses.length > maxItems;

      // If too many results and not showing all, show first few + any expanded group
      const fewResultsNb = Number(process.env.REACT_APP_LEFT_LIST_FEW_RES_NB) || 3;
      let addresses = allAddresses;
      if (tooManyResults && !showAllResults) {
        const firstFew = allAddresses.slice(0, fewResultsNb);
        // Only include expanded addresses that still exist in current results
        const expandedNotInFirstFew = activeCollapseKeys.filter(
          (key) => !firstFew.includes(key) && allAddresses.includes(key)
        );
        addresses = [...firstFew, ...expandedNotInFirstFew];
      }

      // Filter out any addresses that don't exist in groupedByAddress (safety check)
      addresses = addresses.filter((addr) => groupedByAddress[addr]);

      const items: CollapseProps["items"] = addresses.map(
        (address) => ({
          key: address,
          label: address,
          children: (
            <div className="flex flex-col gap-2">
              {loadingAddress === address ? (
                // Show placeholder skeleton items based on expected count
                Array.from({ length: loadingCount }).map((_, idx) => (
                  <div
                    key={`placeholder-${idx}`}
                    className="bg-gray-200 rounded-[10px] p-2 h-[80px] animate-pulse"
                  />
                ))
              ) : (
                groupedByAddress[address].map((item: any, itemIndex: number) => (
                  <div key={itemIndex} data-ademe-id={item.ademe_id}>
                    <AddressItem item={item} isIframe={isIframe} />
                  </div>
                ))
              )}
            </div>
          ),
          extra: (
            <>
              {loadingAddress === address ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />
              ) : (
                <>
                  <p>{groupedByAddress[address].reduce((sum: number, item: any) => sum + (item.count || 1), 0)} Résultats</p>
                  <DownOutlined />
                </>
              )}
            </>
          ),
        }),
      );

      // All collapsed by default - user expands by clicking title or map marker
      const effectiveKeys = activeCollapseKeys;

      console.log("Rendering with debugInfo:", debugInfo);
      return (
        <div ref={listRef} className="overflow-y-auto flex-1 min-h-0">
          {(debugInfo?.sql_description || debugInfo?.sql) && (
            <div className="px-3 py-2 mb-2 bg-gray-50 rounded-lg text-sm text-gray-700 flex items-start gap-2">
              {debugInfo.sql_description && (
                <span className="flex-1 italic">{debugInfo.sql_description}</span>
              )}
              {debugInfo.sql && (
                <Popover
                  content={
                    <div className="max-w-[400px] max-h-[300px] overflow-auto text-xs">
                      <pre className="whitespace-pre-wrap">{debugInfo.sql}</pre>
                      {wsLogs.length > 0 && (
                        <>
                          <div className="mt-2 pt-2 border-t border-gray-200 font-semibold text-gray-600">Logs de la recherche</div>
                          <pre className="whitespace-pre-wrap">{wsLogs.join("\n")}</pre>
                        </>
                      )}
                    </div>
                  }
                  title="Requête SQL"
                  trigger="click"
                  placement="bottomRight"
                >
                  <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 cursor-pointer mt-0.5 flex-shrink-0 ml-auto" />
                </Popover>
              )}
            </div>
          )}
          <Collapse
            activeKey={effectiveKeys}
            onChange={(keys) => handleCollapseChange(keys as string[])}
            expandIcon={() => <IconLocation />}
            items={items}
          />
          {tooManyResults && !showAllResults && (
            <div
              className="p-3 mt-2 text-center bg-orange-50 rounded-lg text-sm cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => setShowAllResults(true)}
            >
              <div className="text-green-dark font-medium">
                Voir tout ({allAddresses.length - addresses.length} adresses restantes)
              </div>
              <div className="text-orange-600 text-xs mt-1">
                <WarningOutlined className="mr-1" />
                Peut ralentir votre navigateur
              </div>
            </div>
          )}
                  </div>
      );
    }
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {(debugInfo?.sql_description || debugInfo?.sql) && (
          <div className="px-3 py-2 mb-2 bg-gray-50 rounded-lg text-sm text-gray-700 flex items-start gap-2">
            {debugInfo.sql_description && (
              <span className="flex-1 italic">{debugInfo.sql_description}</span>
            )}
            {debugInfo.sql && (
              <Popover
                content={
                  <div className="max-w-[400px] max-h-[300px] overflow-auto text-xs">
                    <pre className="whitespace-pre-wrap">{debugInfo.sql}</pre>
                    {wsLogs.length > 0 && (
                      <>
                        <div className="mt-2 pt-2 border-t border-gray-200 font-semibold text-gray-600">Logs de la recherche</div>
                        <pre className="whitespace-pre-wrap">{wsLogs.join("\n")}</pre>
                      </>
                    )}
                  </div>
                }
                title="Requête SQL"
                trigger="click"
                placement="bottomRight"
              >
                <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 cursor-pointer mt-0.5 flex-shrink-0 ml-auto" />
              </Popover>
            )}
          </div>
        )}
        <Empty className="flex-1 flex flex-col items-center justify-center pb-[30vh] lg:pb-0" />
      </div>
    );
  };

  const getMapCenter = () => {
    if (center.length >= 2) {
      return { lat: center[1], lng: center[0] };
    }
    if (gps.length > 0 && gps[0]?.lat && gps[0]?.lng) {
      return { lat: gps[0].lat, lng: gps[0].lng };
    }
    return ORLEANS_CENTER;
  };

  if (!isLoggedIn && !hasIframeKeyAccess && !isPublicAccess) {
    return (
      <div className="energy-address">
        <div className="w-full h-full flex items-center justify-center relative">
          <div className="absolute inset-0 z-0">
            <MapContainer
              center={ORLEANS_CENTER}
              zoom={ULTRA_HIGH_ZOOM}
              scrollWheelZoom={false}
              zoomControl={false}
              dragging={false}
              className="h-full w-full opacity-50"
              attributionControl={false}
            >
              <AttributionControl prefix={false} />
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={Number(process.env.REACT_APP_MAX_ZOOM) || 20}
              />
            </MapContainer>
          </div>
          <div className="relative z-10 bg-white rounded-[30px] p-8 shadow-xl max-w-md text-center">
            <LockOutlined className="text-5xl text-green-dark mb-4" />
            <h2 className="text-2xl font-bold text-green-dark mb-3">
              Accès réservé aux membres
            </h2>
            <p className="text-gray-600 mb-6">
              Connectez-vous pour accéder à la carte interactive des DPE et découvrir les performances énergétiques autour de vous.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to={PAGES.login}
                onClick={() => setLastAccessPage(window.location.hash?.slice(1))}
              >
                <Button type="primary" size="large" block className="bg-green-dark">
                  Se connecter
                </Button>
              </Link>
              <Link
                to={PAGES.signup}
                onClick={() => setLastAccessPage(window.location.hash?.slice(1))}
              >
                <Button size="large" block>
                  Créer un compte
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("energy-address flex-col", { "energy-address--iframe": isIframe, "energy-address--address-page": defaultSearchMode === 'address' && !isIframe })}
      style={defaultSearchMode === 'address' ? ({ "--address-primary": ADDRESS_PRIMARY_COLOR } as React.CSSProperties) : undefined}
    >
      {/* Search bar - top on desktop, hidden on mobile (moved inside flex) */}
      <div className="w-full px-3 py-2 sm:px-6 sm:py-4 hidden lg:block">
        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-stretch bg-[#F2F4F5] rounded-full overflow-hidden border border-gray-200">
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'ai',
                    icon: <RobotOutlined />,
                    label: 'Recherche IA',
                    onClick: () => { setSearchMode('ai'); setSearchValue(''); setDroppedFile(null); setSelectedAddressCoords(null); },
                  },
                  {
                    key: 'address',
                    icon: <EnvironmentOutlined />,
                    label: 'Recherche par adresse',
                    onClick: () => { setSearchMode('address'); setSearchValue(''); setDroppedFile(null); },
                  },
                  {
                    key: 'file',
                    icon: <FileOutlined />,
                    label: 'Fichier',
                    onClick: () => { setSearchMode('file'); setSearchValue(''); setDroppedFile(null); },
                  },
                ],
                selectedKeys: [searchMode],
              }}
              trigger={defaultSearchMode === 'address' ? [] : ['click']}
              disabled={defaultSearchMode === 'address'}
            >
              <div className={`flex items-center gap-1 px-3 border-r border-gray-300 ${defaultSearchMode === 'address' ? 'cursor-default opacity-70' : 'cursor-pointer hover:bg-gray-200'} transition-colors`}>
                {searchMode === 'ai' ? (
                  <RobotOutlined className="text-green-dark" />
                ) : searchMode === 'address' ? (
                  <EnvironmentOutlined className="text-green-dark" />
                ) : (
                  <FileOutlined className="text-green-dark" />
                )}
                <span className="text-sm text-green-dark font-medium hidden md:inline">
                  {searchMode === 'ai' ? 'IA' : searchMode === 'address' ? 'Adresse' : 'Fichier'}
                </span>
                {defaultSearchMode !== 'address' && <DownOutlined className="text-xs text-gray-400" />}
              </div>
            </Dropdown>
{searchMode === 'file' ? (
              <div
                className={`flex-1 flex items-center justify-center py-2 px-4 cursor-pointer transition-colors ${isDragging ? 'bg-green-100' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    setDroppedFile(file);
                    setSearchValue(file.name);
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.heic,.heif"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setDroppedFile(file);
                      setSearchValue(file.name);
                    }
                  }}
                />
                <span className={`text-sm ${droppedFile ? 'text-green-dark font-medium' : 'text-gray-400'}`}>
                  {droppedFile ? droppedFile.name : 'Glissez un fichier ou cliquez ici...'}
                </span>
              </div>
            ) : (
              <div className="flex-1 flex items-center relative">
                <input
                  ref={desktopInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    if (searchMode === 'address') {
                      setAddressSearchValue(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsSearchFocused(false);
                      handleSearch(searchValue);
                    }
                  }}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={(e) => {
                    const relatedTarget = e.relatedTarget as HTMLElement;
                    if (relatedTarget?.closest('.suggestions-dropdown')) {
                      return;
                    }
                    setTimeout(() => setIsSearchFocused(false), 150);
                  }}
                  className="w-full py-2 px-3 bg-transparent border-0 text-green-dark outline-none"
                  placeholder={searchMode === 'ai' ? "DPE récents, passoires thermiques..." : "12 rue Victor Hugo, Lyon..."}
                />
                {searchValue && (
                  <CloseCircleOutlined
                    className="text-gray-400 hover:text-gray-600 cursor-pointer absolute right-2"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setSearchValue('')}
                  />
                )}
              </div>
            )}
            {searchMode !== 'address' && !isIframe && (
              <Button
                type="text"
                icon={<MoreOutlined rotate={90} />}
                onClick={() => setIsSettingsOpen(true)}
                className="border-0 text-white hover:!text-white !rounded-none h-auto !bg-[var(--address-primary,#387270)] flex-shrink-0"
              />
            )}
            <Button
              type="primary"
              onClick={() => handleSearch(searchValue)}
              className={`!rounded-r-full h-auto px-3 md:px-5 flex items-center flex-shrink-0 ${searchMode === 'address' ? '!rounded-l-none' : '!rounded-l-none'}`}
              icon={<SearchOutlined />}
              disabled={searchMode === 'file' && !droppedFile}
            >
              <span className="hidden md:inline">Rechercher</span>
            </Button>
          </div>
          {isSearchFocused && (
            <div className="suggestions-dropdown absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
              {searchMode === 'ai' ? (
                <>
                  <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
                    Suggestions IA
                  </div>
                  {AI_SEARCH_SUGGESTIONS.map((suggestion: string, index: number) => (
                    <div
                      key={index}
                      className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-700 transition-colors"
                      onMouseDown={() => {
                        setSearchValue(suggestion);
                        setIsSearchFocused(false);
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </>
              ) : searchValue.length >= 3 ? (
                <>
                  {isFetchingAutoComplete && (
                    <div className="px-3 py-2 flex items-center gap-2 text-sm text-gray-400 border-b border-gray-100">
                      <LoadingOutlined className="text-green-dark" />
                      <span>Recherche...</span>
                    </div>
                  )}
                  {addressAutoCompleteOptions.length > 0 ? (
                    addressAutoCompleteOptions.map((option: any, index: number) => {
                      const coords = option.data?.geometry?.coordinates;
                      const addressCoords = coords ? { lat: coords[1], lon: coords[0] } : undefined;
                      return (
                        <div
                          key={index}
                          className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-700 transition-colors"
                          onMouseDown={() => {
                            setSearchValue(option.value);
                            setIsSearchFocused(false);
                            if (addressCoords) {
                              setSelectedAddressCoords(addressCoords);
                            }
                            handleSearch(option.value, addressCoords);
                          }}
                        >
                          {option.label}
                        </div>
                      );
                    })
                  ) : !isFetchingAutoComplete ? (
                    <div className="px-3 py-2 text-sm text-gray-400">
                      Aucun résultat
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">
                  Tapez au moins 3 caractères
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-0 lg:gap-6 px-0 lg:px-6 pb-0 lg:pb-6">
        {/* Mobile search bar - between map and results */}
        <div className="w-full order-2 lg:hidden px-1 py-2" ref={mobileSearchRef}>
          <div className="relative">
            <div className="flex items-stretch bg-[#F2F4F5] rounded-full overflow-hidden border border-gray-200">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'ai',
                      icon: <RobotOutlined />,
                      label: 'Recherche IA',
                      onClick: () => { setSearchMode('ai'); setSearchValue(''); setDroppedFile(null); setSelectedAddressCoords(null); },
                    },
                    {
                      key: 'address',
                      icon: <EnvironmentOutlined />,
                      label: 'Recherche par adresse',
                      onClick: () => { setSearchMode('address'); setSearchValue(''); setDroppedFile(null); },
                    },
                    {
                      key: 'file',
                      icon: <FileOutlined />,
                      label: 'Fichier',
                      onClick: () => { setSearchMode('file'); setSearchValue(''); setDroppedFile(null); },
                    },
                  ],
                  selectedKeys: [searchMode],
                }}
                trigger={defaultSearchMode === 'address' ? [] : ['click']}
                disabled={defaultSearchMode === 'address'}
              >
                <div className={`flex items-center px-3 border-r border-gray-300 ${defaultSearchMode === 'address' ? 'cursor-default opacity-70' : 'cursor-pointer hover:bg-gray-200'} transition-colors`}>
                  {searchMode === 'ai' ? (
                    <RobotOutlined className="text-green-dark text-lg" />
                  ) : searchMode === 'address' ? (
                    <EnvironmentOutlined className="text-green-dark text-lg" />
                  ) : (
                    <FileOutlined className="text-green-dark text-lg" />
                  )}
                </div>
              </Dropdown>
              {searchMode === 'file' ? (
                <div
                  className={`flex-1 flex items-center justify-center py-2 px-3 cursor-pointer transition-colors ${isDragging ? 'bg-green-100' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      setDroppedFile(file);
                      setSearchValue(file.name);
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className={`text-xs ${droppedFile ? 'text-green-dark font-medium' : 'text-gray-400'}`}>
                    {droppedFile ? droppedFile.name : 'Glissez un fichier...'}
                  </span>
                </div>
              ) : (
                <div className="flex-1 flex items-center relative">
                  <input
                    ref={mobileInputRef}
                    type="text"
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (searchMode === 'address') {
                        setAddressSearchValue(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsSearchFocused(false);
                        handleSearch(searchValue);
                      }
                    }}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={(e) => {
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      if (relatedTarget?.closest('.suggestions-dropdown')) {
                        return;
                      }
                      setTimeout(() => setIsSearchFocused(false), 150);
                    }}
                    className="w-full py-2 bg-transparent border-0 text-green-dark outline-none text-sm"
                    placeholder={searchMode === 'ai' ? "DPE récents, passoires..." : "12 rue Victor Hugo..."}
                  />
                  {searchValue && (
                    <CloseCircleOutlined
                      className="text-gray-400 hover:text-gray-600 cursor-pointer absolute right-2"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setSearchValue('')}
                    />
                  )}
                </div>
              )}
              {searchMode !== 'address' && !isIframe && (
                <Button
                  type="text"
                  icon={<MoreOutlined rotate={90} />}
                  onClick={() => setIsSettingsOpen(true)}
                  className="border-0 text-white hover:!text-white !rounded-none h-auto !bg-[var(--address-primary,#387270)] flex-shrink-0"
                />
              )}
              <Button
                type="primary"
                onClick={() => handleSearch(searchValue)}
                className="!rounded-l-none !rounded-r-full h-auto px-4 py-2 flex items-center shadow-md flex-shrink-0"
                disabled={searchMode === 'file' && !droppedFile}
              >
                <SearchOutlined className="text-xl" style={{ transform: 'scale(1.1)' }} />
              </Button>
            </div>
            {isSearchFocused && (
              <div className="suggestions-dropdown absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                {searchMode === 'ai' ? (
                  <>
                    <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
                      Suggestions IA
                    </div>
                    {AI_SEARCH_SUGGESTIONS.slice(0, 4).map((suggestion: string, index: number) => (
                      <div
                        key={index}
                        className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-700 transition-colors"
                        onMouseDown={() => {
                          setSearchValue(suggestion);
                          setIsSearchFocused(false);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </>
                ) : searchValue.length >= 3 ? (
                  <>
                    {isFetchingAutoComplete && (
                      <div className="px-3 py-2 flex items-center gap-2 text-sm text-gray-400 border-b border-gray-100">
                        <LoadingOutlined className="text-green-dark" />
                        <span>Recherche...</span>
                      </div>
                    )}
                    {addressAutoCompleteOptions.length > 0 ? (
                      addressAutoCompleteOptions.slice(0, 4).map((option: any, index: number) => {
                        const coords = option.data?.geometry?.coordinates;
                        const addressCoords = coords ? { lat: coords[1], lon: coords[0] } : undefined;
                        return (
                          <div
                            key={index}
                            className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-700 transition-colors"
                            onMouseDown={() => {
                              setSearchValue(option.value);
                              setIsSearchFocused(false);
                              if (addressCoords) {
                                setSelectedAddressCoords(addressCoords);
                              }
                              handleSearch(option.value, addressCoords);
                            }}
                          >
                            {option.label}
                          </div>
                        );
                      })
                    ) : !isFetchingAutoComplete ? (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        Aucun résultat
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    Tapez au moins 3 caractères
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
      <div className={classNames(
        "basis-2/5 bg-white rounded-[20px] lg:rounded-[30px] order-3 lg:order-1 flex flex-col p-4 sm:p-6 h-auto lg:min-h-0 mx-1 lg:mx-0 mt-2 lg:mt-0 mb-4 lg:mb-0",
        { "border border-gray-200": isIframe }
      )}>
        <Modal
          title="Paramètres de recherche"
          open={isSettingsOpen}
          onCancel={() => setIsSettingsOpen(false)}
          onOk={() => setIsSettingsOpen(false)}
          okText="Appliquer"
          cancelText="Annuler"
          zIndex={1100}
        >
          <div className="flex flex-col gap-4 py-4">
            {/* Model selection hidden for now
            <div className="flex items-center gap-4">
              <label className="font-medium w-24">Modèle :</label>
              <Select
                value={model}
                onChange={_setModel}
                className="flex-1"
                options={[
                  { value: "gpt-5", label: "gpt-5" },
                  { value: "gpt-5-mini", label: "gpt-5-mini" },
                  { value: "gpt-5-nano", label: "gpt-5-nano" },
                  { value: "gpt-4o-realtime-preview", label: "gpt-4o-realtime-preview" },
                ]}
              />
            </div>
            */}
            <div className="flex items-center gap-4">
              <label className="font-medium flex-1">Logs temps réel :</label>
              <Switch
                checked={realTimeLogs}
                onChange={(checked) => {
                  setRealTimeLogs(checked);
                  localStorage.setItem("aiMap_realTimeLogs", String(checked));
                }}
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="font-medium w-24">Batch size :</label>
              <InputNumber
                min={100}
                max={50000}
                step={500}
                value={batchSize}
                onChange={(val) => setBatchSize(val || 5000)}
                className="flex-1"
              />
            </div>
            <div className="text-xs text-gray-400 text-right mt-2">
              Version : {frontendVersion || "dev"}
            </div>
          </div>
        </Modal>
        {/* Hidden for now - was interfering with scroll behavior
        <p className="font-semibold text-base mb-6">
          Sélectionnez le DPE que vous
          <br /> souhaitez analyser dans la liste ci-dessous :
          {queryInfo && (
            <Popover
              content={
                <pre style={{ maxWidth: 500, maxHeight: 400, overflow: "auto", fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {queryInfo
                    .replace(/\bSELECT\b/gi, "\nSELECT")
                    .replace(/\bFROM\b/gi, "\nFROM")
                    .replace(/\bWHERE\b/gi, "\nWHERE")
                    .replace(/\bAND\b/gi, "\n  AND")
                    .replace(/\bOR\b/gi, "\n  OR")
                    .replace(/\bORDER BY\b/gi, "\nORDER BY")
                    .replace(/\bGROUP BY\b/gi, "\nGROUP BY")
                    .replace(/\bLIMIT\b/gi, "\nLIMIT")
                    .replace(/\bJOIN\b/gi, "\nJOIN")
                    .replace(/\bLEFT JOIN\b/gi, "\nLEFT JOIN")
                    .replace(/\bINNER JOIN\b/gi, "\nINNER JOIN")
                    .trim()}
                </pre>
              }
              title="SQL"
              trigger={["hover", "click"]}
            >
              <InfoCircleOutlined className="ml-2 text-gray-500 cursor-pointer" />
            </Popover>
          )}
        </p>
        */}

        {renderGPSItem()}

        {/* Distribution chart - hidden on mobile and in address mode */}
        <div className="hidden lg:block">
          {isPendingSearch && defaultSearchMode !== 'address' ? (
            <div className="shrink-0 mt-4">
              <div className="flex justify-between">
                <div className="w-full">
                  <h3 className="text-base text-green-dark font-semibold">Distribution des DPE</h3>
                  <p className="desc text-gray-400 text-sm">Chargement...</p>
                </div>
              </div>
              <div className="bar-chart flex items-end justify-center gap-1">
                {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((label, i) => (
                  <div key={label} className="flex flex-col items-center">
                    <div
                      className="w-9 rounded-lg bg-gray-200 animate-pulse"
                      style={{ height: `${[25, 35, 45, 40, 30, 20, 15][i]}px` }}
                    />
                    <span className="text-sm font-bold mt-1 text-gray-300">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : distributionData && !hasExpandedPanel && defaultSearchMode !== 'address' ? (
            <div className="shrink-0 mt-4">
              {showExportOptions ? (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-base text-green-dark font-semibold">Exporter les données</h3>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => setShowExportOptions(false)}
                      className="text-gray-400"
                    >
                      ✕
                    </Button>
                  </div>
                  <div className="flex gap-3">
                    <div
                      className="flex-1 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-green-dark hover:bg-green-50 transition-colors"
                      onClick={handleExportByAddress}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <DownloadOutlined className="text-green-dark" />
                        <span className="font-medium text-green-dark">Par adresse</span>
                      </div>
                      <p className="text-xs text-gray-500">{visibleResults.length} adresses</p>
                    </div>
                    <div
                      className="flex-1 p-3 border border-gray-200 rounded-lg cursor-not-allowed opacity-60"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <LockOutlined className="text-gray-400" />
                        <span className="font-medium text-gray-500">Par DPE - Premium</span>
                      </div>
                      <p className="text-xs text-gray-400">Détails complets : surface, date, GES, consommations...</p>
                      <p className="text-xs text-gray-400 mt-1">Bientôt disponible • {distributionData.distribution_neighbors.subtitle}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <DistributionChartInteger
                  dataHomeUploaded={distributionData}
                  isPendingUploaded={isPendingSearch}
                  chartHeight={50}
                  extra={
                    <Button
                      type="text"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => setShowExportOptions(true)}
                      className="text-gray-500 hover:text-green-dark"
                    >
                      <span className="hidden md:inline">CSV</span>
                    </Button>
                  }
                />
              )}
            </div>
          ) : !distributionData ? (
            <div className="info-box shrink-0 mt-4">
              <p>
                S'il n'apparaît pas, le DPE n'a peut être pas encore été réalisé
                ou déposé
              </p>

              <p>
                sur la base de l'ADEME, vous pouvez le demander à votre vendeur.
              </p>

              <p>
                Vous pouvez nous contacter pour plus d'informations :{" "}
                <b>
                  <a href="mailto:contact@kardino.fr">contact@kardino.fr</a>
                </b>
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="w-full basis-3/5 order-1 lg:order-2 flex flex-col min-h-[55vh] lg:min-h-0">
        {/* Progress Steps - hidden for AI search mode */}
        {searchMode !== 'ai' && (
          <div className="hidden lg:block bg-white rounded-[30px] border-4 border-white shadow-sm mb-4 p-4">
            <div className="flex gap-2">
              {progressSteps.map((item, index) => (
                <div className="basis-1/4" key={index}>
                  <div
                    className={classNames(
                      "rounded-[10px] h-1 w-full mb-1.5",
                      item.isFinished ? "bg-[#073034]" : "bg-[#07303433]"
                    )}
                  />
                  <p
                    className={classNames(
                      "font-semibold text-sm",
                      item.isFinished ? "text-[#073034]" : "text-[var(--address-primary,#387270)]"
                    )}
                  >
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="map-wrapper flex-1 relative !p-0 !rounded-[20px] lg:!rounded-[30px] overflow-hidden !bg-transparent border-4 border-white">
          {versionMismatch && (
            <div className="absolute top-0 left-0 right-0 z-[1001] bg-orange-500 text-white px-4 py-2 text-sm text-center">
              Votre version ({frontendVersion || '?'}) est obsolète, rechargez votre page (⌘+Shift+R sur Mac, Ctrl+Shift+R sur PC)
            </div>
          )}
          {wsNotif && (
            <div
              className="absolute left-0 right-0 z-[1001] bg-gray-600 text-white px-4 py-2 text-sm text-center"
              style={{ top: `${topBarOffset}rem` }}
            >
              <div
                className="absolute top-0 left-0 h-1 bg-white/50"
                style={{
                  animation: `shrink ${Number(process.env.REACT_APP_NOTIF_TIME) || 5000}ms linear forwards`,
                }}
              />
              <style>{`@keyframes shrink { from { width: 100%; } to { width: 0%; } }`}</style>
              {wsNotif}
            </div>
          )}
          {searchNotification && (
            <div className="absolute left-1/2 transform -translate-x-1/2 z-[1000] bg-green-dark text-white px-4 py-2 rounded-full shadow-lg text-sm" style={{ top: `${topBarOffset + 0.5}rem` }}>
              {searchNotification}
            </div>
          )}
          {searchStats && !searchNotification && (
            <div
              className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 z-[999] bg-white/90 text-gray-700 px-3 py-1 rounded-full shadow text-xs"
              style={{ top: `${topBarOffset + 0.5}rem` }}
            >
              {defaultSearchMode === 'address'
                ? 'base DPEs'
                : <>{searchStats.totalDpes} DPE{searchStats.totalDpes > 1 ? 's' : ''}{searchStats.surfaceStr ? ` sur ${searchStats.surfaceStr}` : ''}</>} — <span className="font-bold"> au {sqlUpdateLast}</span>
              {searchStats.isTruncated && (
                <span className="text-red-600 font-semibold ml-2">— des résultats sont manquants!</span>
              )}
            </div>
          )}
          {showRefreshButton && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[1000]">
              <Button
                type="primary"
                onClick={() => {
                  setShowRefreshButton(false);
                  handleSearch(searchValue, undefined, true, searchMode === 'address', true);
                }}
                className="shadow-lg"
              >
                Mettre à jour la carte
              </Button>
            </div>
          )}
          <div className="absolute bottom-4 left-4 z-[1000]">
            <Button
              type="default"
              icon={<AimOutlined />}
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords;
                      if (mapRef.current) {
                        mapRef.current.setView([latitude, longitude], 17);
                      }
                    },
                    (error) => {
                      console.error('Geolocation error:', error);
                      setSearchError('Impossible d\'accéder à votre position');
                    }
                  );
                } else {
                  setSearchError('La géolocalisation n\'est pas supportée');
                }
              }}
              className="shadow-lg bg-white hover:bg-gray-50"
              title="Ma position"
            />
          </div>
          {searchError && (
            <div
              className="absolute left-0 right-0 z-[1001] bg-red-500 text-white px-4 py-2 text-sm text-center flex items-center justify-center gap-2"
              style={{ top: versionMismatch ? '2.5rem' : 0 }}
            >
              <WarningOutlined />
              {searchError}
              <button
                onClick={() => setSearchError(null)}
                className="ml-2 hover:opacity-80 absolute right-4"
              >
                ✕
              </button>
            </div>
          )}
          {isPendingSearch && !searchError && (
            <div className="absolute inset-0 z-[1000] flex items-end justify-center pb-[20%] lg:items-center lg:pb-0 bg-white/50">
              <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} size="large" />
            </div>
          )}
          <MapContainer
            center={getMapCenter()}
            zoom={ULTRA_HIGH_ZOOM}
            scrollWheelZoom={false}
            ref={mapRef}
            attributionControl={false}
          >
            <AttributionControl prefix={false} />
            <TileLayer
              url={CARTO_TILE_URL}
              attribution={CARTO_ATTRIBUTION}
              maxZoom={Number(process.env.REACT_APP_MAX_ZOOM) || 20}
            />
            <MapBoundsHandler onBoundsChange={handleBoundsChange} />
            <MarkerClusterGroup
              ref={clusterRef}
              chunkedLoading
              maxClusterRadius={60}
              showCoverageOnHover={false}
              iconCreateFunction={createClusterIcon}
            >
              {gps.map((item) => {
                const hasMultiple = (item.count || 1) > 1;
                const customIcon = L.divIcon({
                  className: `custom-div-icon dpe-${item.label} dpe-count-${item.count || 1}`,
                  html: ReactDOMServer.renderToString(
                    <div
                      data-id={item.ademe_id}
                      className={classNames(
                        "label-container min-h-[30px] rounded-3xl flex items-center justify-center px-2",
                        {
                          "label-container--selected":
                            item.ademe_id === selectedLabelId,
                        },
                      )}
                      style={{
                        background: ADDRESS_MAP_COLOR_DPE[item.label],
                        zIndex: 5,
                        minWidth: "38px",
                        fontSize: hasMultiple ? "14px" : "12px",
                      }}
                    >
                      <span className="label-text font-medium whitespace-nowrap text-white" style={{ marginTop: "2px" }}>
                        {hasMultiple ? item.count : item.label}
                      </span>
                    </div>,
                  ),
                  iconSize: [36, 36],
                });

                const isSelected = item.ademe_id === selectedLabelId;
                return (
                  <Marker
                    position={item}
                    // Keyed by content, not index: the cluster group only recounts on marker add/remove,
                    // not on setIcon - an index key reused markers and left stale cluster numbers
                    key={`${item.lat}-${item.lng}-${item.label}-${item.count || 1}`}
                    icon={customIcon}
                    zIndexOffset={isSelected ? 10000 : item.label_z}
                    eventHandlers={{
                      click: () => handleClickLabel(item),
                    }}
                    ref={(ref) => {
                      if (!item.ademe_id) return;
                      if (ref) {
                        markerRefs.current.set(item.ademe_id, ref);
                      } else {
                        markerRefs.current.delete(item.ademe_id);
                      }
                    }}
                  />
                );
              })}
            </MarkerClusterGroup>

            {/* Blue pin for address search mode */}
            {searchMode === 'address' && selectedAddressCoords && (
              <Marker
                position={[selectedAddressCoords.lat, selectedAddressCoords.lon]}
                zIndexOffset={20000}
              />
            )}

            {searchBbox && searchBbox.length === 2 && (
              <Rectangle
                bounds={searchBbox}
                pathOptions={{
                  color: "#073034",
                  weight: 2,
                  dashArray: "8, 8",
                  fill: false,
                }}
              />
            )}

          </MapContainer>
        </div>
      </div>
      </div>

      {/* Floating Insight Button - hidden for now, using header "l'Expert" button instead */}
      {/* <div
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#D8FF7F] rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-shadow z-50"
        onClick={handleToInsight}
      >
        <IconStarContact className="w-7 h-7" style={{ fill: "#073034" }} />
      </div> */}

      {/* Detached Chat Panel - Right side */}
      {isInsightModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-[9999]"
            onClick={() => setIsInsightModalOpen(false)}
          />
          <div className="fixed top-[60px] bottom-[60px] right-6 w-[380px] bg-white rounded-3xl shadow-2xl z-[10000] overflow-hidden flex flex-col">
            <div
              className="absolute top-3 right-3 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center cursor-pointer z-10 hover:bg-gray-100 transition-colors"
              onClick={() => setIsInsightModalOpen(false)}
            >
              <CloseOutlined className="text-green-dark text-sm" />
            </div>
            <InsightPage />
          </div>
        </>
      )}
    </div>
  );
}

export default AiMapPage;
